import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Square,
  DoorOpen,
  DoorClosed,
  MoveHorizontal,
  UserRound,
  Mail,
  Phone,
  X,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { useQuotes } from '../context/QuotesContext';
import ContactSearchPicker from '../components/ContactSearchPicker';
import {
  buildConfiguratorNavigationState,
  clearActiveContactForConfigurator,
  persistActiveContactForConfigurator,
  resolveConfiguratorContactState,
} from '../lib/contactConfigurator';

const getContactInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const ConfiguratorSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { startNewQuoteSession } = useQuotes();
  const returnTo = location.state?.returnTo;
  const [activeContact, setActiveContact] = useState(null);

  useEffect(() => {
    if (location.state?.newQuote && !location.state?.preserveQuoteSession) {
      startNewQuoteSession();
    }
  }, [location.state?.newQuote, location.state?.preserveQuoteSession, startNewQuoteSession]);

  useEffect(() => {
    if (authLoading) return;
    setActiveContact(resolveConfiguratorContactState(location.state, user?.id));
  }, [authLoading, location.state, user?.id]);

  const handleBack = () => {
    if (location.state?.fromContactProfile && returnTo) {
      navigate(returnTo);
      return;
    }
    navigate('/');
  };

  const handleContactSelect = (contact) => {
    const payload = persistActiveContactForConfigurator(contact, user?.id);
    setActiveContact(payload);
  };

  const handleClearContact = () => {
    clearActiveContactForConfigurator();
    setActiveContact(null);
  };

  const handleSelectConfigurator = (path) => {
    navigate(path, {
      state: buildConfiguratorNavigationState(
        location.state,
        { selectedContact: activeContact },
        user?.id
      ),
    });
  };

  const configuratorTypes = [
    {
      id: 'windows',
      nameKey: 'configurator.kozijnen',
      descriptionKey: 'configurator.kozijnenDesc',
      icon: Square,
      color: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      path: '/kozijnen',
    },
    {
      id: 'patio-door',
      nameKey: 'configurator.terrasdeur',
      descriptionKey: 'configurator.terrasdeurDesc',
      icon: DoorOpen,
      color: 'bg-green-100',
      iconColor: 'text-green-600',
      borderColor: 'border-green-500',
      path: '/terrasdeur',
    },
    {
      id: 'back-door',
      nameKey: 'configurator.achterdeur',
      descriptionKey: 'configurator.achterdeurDesc',
      icon: DoorClosed,
      color: 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-500',
      path: '/achterdeur',
    },
    {
      id: 'front-door',
      nameKey: 'configurator.voordeur',
      descriptionKey: 'configurator.voordeurDesc',
      icon: DoorClosed,
      color: 'bg-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-500',
      path: '/voordeur',
    },
    {
      id: 'sliding-door',
      nameKey: 'configurator.schuifpui',
      descriptionKey: 'configurator.schuifpuiDesc',
      icon: MoveHorizontal,
      color: 'bg-teal-100',
      iconColor: 'text-teal-600',
      borderColor: 'border-teal-500',
      path: '/schuifpui',
    },
    {
      id: 'keralit',
      nameKey: 'configurator.keralit',
      descriptionKey: 'configurator.keralitDesc',
      icon: Square,
      color: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      borderColor: 'border-indigo-500',
      path: '/keralit',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">
                {location.state?.fromContactProfile && returnTo
                  ? t('contactProfile.backToContact')
                  : t('configurator.backToHome')}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('configurator.selectType')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('configurator.selectTypeDescription')}
          </p>
        </div>

        {isAuthenticated && !authLoading ? (
          <div className="mb-10 mx-auto max-w-2xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center gap-3 border-b border-gray-100 bg-slate-50/80 px-6 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                <UserRound className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900">
                  {t('configurator.selectContact')}
                </h2>
                <p className="text-xs text-gray-500">
                  {t('configurator.selectContactDescription')}
                </p>
              </div>
            </div>

            <div className="px-6 py-5">
              {activeContact?.name ? (
                <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-12 w-12 border border-orange-200 bg-white">
                      <AvatarFallback className="bg-orange-500 text-sm font-semibold text-white">
                        {getContactInitials(activeContact.name)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-base font-semibold text-gray-900">
                          {activeContact.name}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                          <CheckCircle2 className="h-3 w-3" />
                          {t('configurator.contactLinkedBadge')}
                        </span>
                      </div>

                      <div className="mt-1.5 space-y-0.5 text-sm text-gray-600">
                        {activeContact.email ? (
                          <p className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">{activeContact.email}</span>
                          </p>
                        ) : null}
                        {activeContact.phone ? (
                          <p className="flex items-center gap-1.5">
                            <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate">{activeContact.phone}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleClearContact}
                      className="shrink-0 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-orange-100 hover:text-orange-700"
                      aria-label={t('configurator.clearContact')}
                      title={t('configurator.clearContact')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-4">
                    <ContactSearchPicker
                      selectedContact={activeContact}
                      onSelect={handleContactSelect}
                      buttonClassName="border-orange-200 bg-white/70 hover:bg-white"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                      <UserRound className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-700">
                        {t('configurator.noContactSelectedTitle')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {t('configurator.noContactSelectedHint')}
                      </p>
                    </div>
                  </div>
                  <ContactSearchPicker
                    selectedContact={activeContact}
                    onSelect={handleContactSelect}
                  />
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {configuratorTypes.map((type) => {
            const Icon = type.icon;
            return (
              <Card
                key={type.id}
                className="group cursor-pointer border-2 hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={() => handleSelectConfigurator(type.path)}
              >
                <CardHeader className="text-center pb-4">
                  <div className={`w-20 h-20 ${type.color} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-10 h-10 ${type.iconColor}`} />
                  </div>
                  <CardTitle className="text-2xl mb-2">{t(type.nameKey)}</CardTitle>
                  <CardDescription className="text-base">
                    {t(type.descriptionKey)}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full bg-slate-800 hover:bg-slate-900 group-hover:bg-orange-600 transition-colors"
                    size="lg"
                  >
                    {t('configurator.configureNow')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="bg-white rounded-xl p-8 border-2 border-gray-200 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-3">
            {t('configurator.notSure')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('configurator.helpDescription')}
          </p>
          <Button variant="outline" size="lg" className="border-2">
            {t('configurator.contactSupport')}
          </Button>
        </div>
      </div>

      <div className="bg-slate-800 py-12 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">5</div>
              <div className="text-slate-300">{t('configurator.productTypes')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">3 Min</div>
              <div className="text-slate-300">{t('configurator.averageConfiguration')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">100%</div>
              <div className="text-slate-300">{t('configurator.accuracyGuaranteed')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguratorSelection;
